/**
 * Every programme the signed-in account can reach, as a list.
 *
 * It was a pair of `select` elements and an Open button: you chose a programme
 * from a dropdown that showed one at a time, so comparing two of them, or
 * seeing which needed attention, meant opening each in turn.
 *
 * @module components/programmes/ProgrammeList
 */

import React from "react";

import { WorkflowStateBadge } from "@/components/shared/WorkflowStateBadge";
import {
  Button,
  type CollectionFiltering,
  CollectionView,
  type Column,
  countLabel,
  EmptyState,
  MutedText,
  type RowAction,
  type RowActionsSpec,
  type SortState,
  Time,
} from "@/components/ui";
import type { ProgrammeSummary } from "@/platform";
import { creditSystemName } from "@/utils/award-types";

export interface ProgrammeListProps {
  programmes: readonly ProgrammeSummary[];
  /** The filter panel, as `useCollectionFilters` returns it. */
  filtering: CollectionFiltering;
  sort: SortState;
  onSortChange: (sort: SortState) => void;
  onOpen: (programmeId: string) => void;
  /** Opens the programme read only, whatever the account could otherwise do. */
  onView: (programmeId: string) => void;
  onEdit: (programmeId: string) => void;
  /** Why Edit is refused on this programme, or nothing when it is offered. */
  editRefusal: (programme: ProgrammeSummary) => string | undefined;
  onOpenTeam: (programmeId: string) => void;
  onOpenModules: (programmeId: string) => void;
  /** Shown instead of the list when nothing loaded at all. */
  emptyAction?: React.ReactNode;
}

function credits(programme: ProgrammeSummary): string {
  const total = Number(programme.totalCredits ?? 0);
  return total > 0 ? `${total} ${creditSystemName(programme.awardType, total)}` : "Credits not set";
}

function level(programme: ProgrammeSummary): string {
  return programme.nfqLevel === null ? "—" : `NFQ ${programme.nfqLevel}`;
}

function updated(programme: ProgrammeSummary): React.ReactNode {
  return programme.updatedAt ? <Time value={programme.updatedAt} variant="date" /> : "—";
}

function cardTitle(programme: ProgrammeSummary): React.ReactElement {
  return (
    <>
      {programme.title}
      <MutedText size="caption" as="div">
        {programme.programmeCode ?? programme.awardType}
      </MutedText>
    </>
  );
}

/**
 * @example
 * ```tsx
 * <ProgrammeList programmes={rows} filtering={filtering} … />
 * ```
 */
export function ProgrammeList({
  programmes,
  filtering,
  sort,
  onSortChange,
  onOpen,
  onView,
  onEdit,
  editRefusal,
  onOpenTeam,
  onOpenModules,
  emptyAction = undefined,
}: ProgrammeListProps): React.ReactElement {
  const rowActions: RowActionsSpec<ProgrammeSummary> = {
    label: (programme) => programme.title,
    testId: (programme) => `programme-actions-${programme.id}`,
    items: (programme) => {
      const refusal = editRefusal(programme);
      const editBase = {
        key: "edit",
        label: "Edit programme",
        icon: "pencil-simple",
        onSelect: () => {
          onEdit(programme.id);
        },
        "data-testid": `programme-edit-${programme.id}`,
      };
      const edit: RowAction = refusal
        ? { ...editBase, disabled: true, disabledReason: refusal }
        : editBase;
      return [
        {
          key: "view",
          label: "View programme",
          icon: "eye",
          onSelect: () => {
            onView(programme.id);
          },
          "data-testid": `programme-view-${programme.id}`,
        },
        edit,
        {
          key: "modules",
          label: "Module studio",
          icon: "squares-four",
          onSelect: () => {
            onOpenModules(programme.id);
          },
        },
        {
          key: "team",
          label: "Programme team",
          icon: "users-three",
          onSelect: () => {
            onOpenTeam(programme.id);
          },
        },
      ];
    },
  };

  const columns: readonly Column<ProgrammeSummary>[] = [
    {
      key: "title",
      header: "Programme",
      // The widest column: titles are what the reader scans for.
      width: "w-2/5 min-w-56",
      // Plain text on a card: the whole card opens the programme, so a button
      // in the title would nest one control in another.
      cardRender: cardTitle,
      sortValue: (programme) => programme.title,
      render: (programme) => (
        <div className="min-w-0">
          <Button
            variant="title"
            onClick={() => {
              onOpen(programme.id);
            }}
            // Not `programme-row-open-…`: that shares a prefix with the row's
            // own testid, so a `^=` selector matches both.
            data-testid={`programme-title-${programme.id}`}
          >
            {programme.title}
          </Button>
          <MutedText size="caption" as="div">
            {programme.programmeCode ?? programme.awardType}
          </MutedText>
        </div>
      ),
    },
    {
      key: "state",
      header: "State",
      width: "w-44",
      card: "footer",
      sortValue: (programme) => programme.workflowState,
      render: (programme) => <WorkflowStateBadge state={programme.workflowState} />,
    },
    { key: "level", header: "Level", sortValue: (p) => p.nfqLevel, render: level, priority: 2 },
    {
      key: "credits",
      header: "Credits",
      cardRender: credits,
      sortValue: (p) => p.totalCredits,
      // The number alone: the unit is the same on every row and belongs to the
      // header, so no row wraps to carry it.
      render: (p) => Number(p.totalCredits ?? 0) || "—",
      align: "end",
      priority: 3,
    },
    {
      key: "discipline",
      header: "Discipline",
      sortValue: (p) => p.discipline,
      render: (p) => p.discipline || "—",
      priority: 3,
    },
    {
      key: "lead",
      header: "Lead",
      sortValue: (p) => p.leadUserName ?? null,
      render: (p) => p.leadUserName ?? "Unassigned",
      priority: 3,
    },
    {
      key: "modules",
      header: "Modules",
      cardRender: (p) => countLabel(p.moduleCount, "module"),
      sortValue: (p) => p.moduleCount,
      render: (p) => p.moduleCount,
      align: "end",
      priority: 2,
    },
    {
      key: "updated",
      header: "Updated",
      sortValue: (p) => p.updatedAt ?? null,
      render: updated,
      priority: 3,
    },
  ];

  const empty = (
    <EmptyState
      icon={filtering.active ? "funnel" : "graduation-cap"}
      title={filtering.active ? "No programmes match these filters" : "No programmes yet"}
      description={
        filtering.active
          ? "Clear the filters to see everything you can reach."
          : "Start one to see it here."
      }
      action={emptyAction}
      density="row"
      data-testid="programme-list-empty"
    />
  );

  return (
    <CollectionView
      storageKey="programmes"
      caption="Programmes you can reach"
      columns={columns}
      rows={programmes}
      rowKey={(programme) => programme.id}
      rowTestId={(programme) => `programme-row-${programme.id}`}
      sort={sort}
      onSortChange={onSortChange}
      emptyState={empty}
      filtering={filtering}
      cardsTestId="programme-list-cards"
      cardTestId={(programme) => `programme-card-${programme.id}`}
      rowActions={rowActions}
      onOpenRow={(programme) => {
        onOpen(programme.id);
      }}
      data-testid="programme-table"
    />
  );
}
